/**
 * Round history (`/me/events/:id/recommend`).
 *
 * This page deliberately renders no candidate on its own: a round has exactly
 * one presentation, the shared `/e/:shareToken/recommendations/:round` route, so
 * the organizer and the participants always read the same numbers.
 */

import { Link, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'

import type { RoundStatus, RoundSummaryDto } from '@shared/types'

import { ErrorBlock, LoadingBlock } from '../../../components/layout/states'
import { Badge, type BadgeProps } from '../../../components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { EmptyState } from '../../../components/ui/primitives'
import { useEventRounds } from '../../../hooks/use-events'
import { formatUnixSeconds } from '../../../lib/format'
import { currentLocale } from '../../../lib/i18n'
import { useEventDetailContext } from './layout'

const ROUND_STATUS_VARIANT: Record<RoundStatus, BadgeProps['variant']> = {
  open: 'default',
  decided: 'success',
}

/** `open` reads as "still collecting" in the shared status vocabulary. */
const ROUND_STATUS_KEY: Record<RoundStatus, string> = {
  open: 'status.collecting',
  decided: 'status.decided',
}

function RoundRow({ shareToken, round }: { shareToken: string; round: RoundSummaryDto }) {
  const { t } = useTranslation()
  const locale = currentLocale()

  return (
    <Link
      to={`/e/${shareToken}/recommendations/${round.round}`}
      className="focus-visible:ring-ring block rounded-xl focus-visible:ring-2 focus-visible:outline-none"
    >
      <Card className="hover:bg-accent/40 h-full transition-colors">
        <CardHeader className="flex-row items-start justify-between gap-3">
          <CardTitle>{t('recommend.roundLabel', { round: round.round })}</CardTitle>
          <Badge variant={ROUND_STATUS_VARIANT[round.status]}>
            {t(ROUND_STATUS_KEY[round.status])}
          </Badge>
        </CardHeader>
        <CardContent className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span>{t('recommend.createdAt', { date: formatUnixSeconds(round.createdAt, locale) })}</span>
          <span>{t('recommend.voteCount', { count: round.voteCount })}</span>
          <span>
            {t('recommend.primary')}
            {': '}
            {round.primary ? round.primary.districtName : t('common.unknown')}
          </span>
        </CardContent>
      </Card>
    </Link>
  )
}

export function EventRecommendPage() {
  const { t } = useTranslation()
  const { event } = useEventDetailContext()
  const rounds = useEventRounds(event.id)
  const location = useLocation()
  // The marker arrives from the run that navigated here, so a reused round is
  // explained where the history it belongs to is shown.
  const reused =
    (location.state as { reused?: boolean; pending?: boolean; round?: number } | null) ?? null

  const sortedRounds = [...(rounds.data?.rounds ?? [])].sort((left, right) => right.round - left.round)

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-base font-semibold">{t('recommend.title')}</h2>

      {reused?.pending ? (
        <Alert variant="info">
          <AlertTitle>{t('recommend.pendingTitle')}</AlertTitle>
          <AlertDescription>{t('recommend.pendingBody')}</AlertDescription>
        </Alert>
      ) : null}

      {reused?.reused ? (
        <Alert variant="info">
          <AlertDescription>
            {t('recommend.reusedTitle')}{' '}
            {reused.round ? t('recommend.reusedBody', { round: reused.round }) : null}
          </AlertDescription>
        </Alert>
      ) : null}

      {rounds.isPending ? <LoadingBlock label={t('common.loading')} /> : null}

      {rounds.isError ? (
        <ErrorBlock
          title={t('error.internal_error')}
          retryLabel={t('common.retry')}
          onRetry={() => void rounds.refetch()}
        />
      ) : null}

      {rounds.data ? (
        sortedRounds.length === 0 ? (
          <EmptyState title={t('recommend.noRounds')} description={t('recommend.noRoundsHint')} />
        ) : (
          <div className="grid gap-3">
            {sortedRounds.map((round) => (
              <RoundRow key={round.round} shareToken={event.shareToken} round={round} />
            ))}
          </div>
        )
      ) : null}
    </section>
  )
}
